import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { reiseApi, type Deltaker, type Sprak } from '../../api/reise';
import { SPRAK } from '../../i18n';

interface Props {
  /** Modulene i reisen, med titlene på kundens språk. */
  moduler: { modulId: string; tittel: string }[];
}

// Enkel kontroll i nettleseren. API-et kontrollerer adressen uansett
const EPOST = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Eieren inviterer en kollega til valgte moduler. Kollegaen får sin egen lenke på e-post. */
export default function Invitasjon({ moduler }: Props) {
  const { t, i18n } = useTranslation();
  const [navn, setNavn] = useState('');
  const [epost, setEpost] = useState('');
  const [rolle, setRolle] = useState('');
  const [valgte, setValgte] = useState<string[]>([]);
  const [sprak, setSprak] = useState<Sprak>((i18n.resolvedLanguage as Sprak) ?? 'nb');
  const [deltakere, setDeltakere] = useState<Deltaker[]>([]);
  const [melding, setMelding] = useState<{ type: 'feil' | 'ok'; tekst: string } | null>(null);
  const [sender, setSender] = useState(false);

  const tittel = (id: string) => moduler.find((m) => m.modulId === id)?.tittel ?? id;

  const hentDeltakere = useCallback(() => {
    reiseApi
      .deltakere()
      .then(setDeltakere)
      .catch(() => setDeltakere([]));
  }, []);

  useEffect(hentDeltakere, [hentDeltakere]);

  async function send(event: FormEvent) {
    event.preventDefault();
    const feil = !navn.trim()
      ? 'reise.invitasjon.skrivNavn'
      : !EPOST.test(epost.trim())
        ? 'reise.invitasjon.skrivEpost'
        : valgte.length === 0
          ? 'reise.invitasjon.velgModul'
          : null;
    if (feil) {
      setMelding({ type: 'feil', tekst: t(feil) });
      return;
    }

    setSender(true);
    setMelding(null);
    try {
      await reiseApi.inviter({ navn: navn.trim(), epost: epost.trim(), rolle: rolle.trim() || undefined, moduler: valgte, sprak });
      setMelding({ type: 'ok', tekst: t('reise.invitasjon.sendt', { epost: epost.trim() }) });
      setNavn('');
      setEpost('');
      setRolle('');
      setValgte([]);
      hentDeltakere();
    } catch (e) {
      const kode = e instanceof ApiFeil ? e.kode : 'ukjent';
      setMelding({ type: 'feil', tekst: t(`feil.${kode}`, { defaultValue: t('feil.ukjent') }) });
    } finally {
      setSender(false);
    }
  }

  return (
    <section aria-labelledby="invitasjon-tittel">
      <h2 id="invitasjon-tittel" tabIndex={-1} className="mb-2 text-[1.375rem] font-semibold">
        {t('reise.invitasjon.tittel')}
      </h2>
      <p className="mb-4 text-slate-700">{t('reise.invitasjon.ingress')}</p>

      <form onSubmit={send} noValidate className="flex flex-col gap-4 kort p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Felt id="kollega-navn" etikett={t('reise.invitasjon.navn')} verdi={navn} onEndre={setNavn} autoComplete="name" />
          <Felt id="kollega-epost" etikett={t('reise.invitasjon.epost')} verdi={epost} onEndre={setEpost} type="email" autoComplete="email" />
          <Felt id="kollega-rolle" etikett={t('reise.invitasjon.rolle')} verdi={rolle} onEndre={setRolle} />
          <div className="flex flex-col">
            <label htmlFor="kollega-sprak" className="mb-1.5 text-sm font-medium text-slate-700">
              {t('reise.invitasjon.sprak')}
            </label>
            <select id="kollega-sprak" value={sprak} onChange={(e) => setSprak(e.target.value as Sprak)} className="felt">
              {SPRAK.map((s) => (
                <option key={s} value={s}>
                  {t(`sprak.${s}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <fieldset>
          <legend className="mb-2 font-medium">{t('reise.invitasjon.moduler')}</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {moduler.map((m) => (
              <label key={m.modulId} className="flex min-h-[3rem] cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-4 py-3">
                <input
                  type="checkbox"
                  checked={valgte.includes(m.modulId)}
                  onChange={() => setValgte((v) => (v.includes(m.modulId) ? v.filter((x) => x !== m.modulId) : [...v, m.modulId]))}
                  className="h-5 w-5 shrink-0"
                />
                <span>{m.tittel}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {melding && (
          <p
            role={melding.type === 'feil' ? 'alert' : 'status'}
            className={
              'rounded-lg px-4 py-3 ' + (melding.type === 'feil' ? 'border-red-200 border-l-4 border-l-red-600 bg-red-50 text-red-900' : 'border-aksent-200 border-l-4 border-l-aksent-600 bg-aksent-50 text-aksent-900')
            }
          >
            {melding.tekst}
          </p>
        )}

        <div>
          <button type="submit" disabled={sender} className="knapp-primaer">
            {t('reise.invitasjon.send')}
          </button>
        </div>
      </form>

      {deltakere.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 font-semibold">{t('reise.invitasjon.deltakere')}</h3>
          <ul className="flex flex-col gap-2">
            {deltakere.map((d) => (
              <li key={d.id} className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="font-medium">
                  {d.navn} <span className="font-normal text-slate-600">· {d.epost}</span>
                </p>
                <p className="text-sm text-slate-700">
                  {d.erEier ? t('reise.invitasjon.eier') : d.moduler.length === 0 ? t('reise.invitasjon.alleModuler') : d.moduler.map(tittel).join(', ')}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

interface FeltProps {
  id: string;
  etikett: string;
  verdi: string;
  onEndre: (verdi: string) => void;
  type?: string;
  autoComplete?: string;
}

function Felt({ id, etikett, verdi, onEndre, type = 'text', autoComplete }: FeltProps) {
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="mb-1.5 text-sm font-medium text-slate-700">
        {etikett}
      </label>
      <input id={id} type={type} value={verdi} autoComplete={autoComplete} onChange={(e) => onEndre(e.target.value)} className="felt" />
    </div>
  );
}
